import { useRef, useCallback, useEffect } from 'react'
import './CanvasViewport.css'

const MIN_ZOOM = 0.8
const MAX_ZOOM = 3
const ZOOM_STEP = 0.25

export function CanvasViewport({ zoom, pan, setZoom, setPan, children, lang = 'he', lockViewport = false, contentSize = null }) {
  const viewportRef = useRef(null)
  const pinchStartRef = useRef(null) // { distance, centerX, centerY, zoom, pan }
  const isInteractingRef = useRef(false)
  const dragStartRef = useRef(null) // { x, y, panX, panY }

  const isTouchDevice = (
    typeof navigator !== 'undefined' &&
    (
      (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 0) ||
      (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
    )
  )

  const clampZoom = useCallback((z) => Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z)), [])

  const zoomIn = useCallback(() => {
    if (lockViewport) return
    setZoom((z) => clampZoom(z + ZOOM_STEP))
  }, [setZoom, clampZoom, lockViewport])

  const zoomOut = useCallback(() => {
    if (lockViewport) return
    setZoom((z) => clampZoom(z - ZOOM_STEP))
  }, [setZoom, clampZoom, lockViewport])

  const zoomReset = useCallback(() => {
    if (lockViewport) return
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [setZoom, setPan, lockViewport])

  // Wheel zoom (at center for simplicity)
  const onWheel = useCallback(
    (e) => {
      if (!viewportRef.current) return
      if (lockViewport) return
      if (isTouchDevice) return
      // Avoid accidental zoom from normal scrolling/trackpad movement.
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
      setZoom((z) => clampZoom(z + delta))
    },
    [setZoom, clampZoom, lockViewport, isTouchDevice]
  )

  // Pinch zoom
  const getTouchDistance = (t1, t2) =>
    Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)
  const getTouchCenter = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  })

  const onTouchStart = useCallback(
    (e) => {
      if (lockViewport) return
      if (e.touches.length === 2) {
        isInteractingRef.current = true
        pinchStartRef.current = {
          distance: getTouchDistance(e.touches[0], e.touches[1]),
          center: getTouchCenter(e.touches[0], e.touches[1]),
          zoom,
          pan: { ...pan },
        }
      }
    },
    [zoom, pan, lockViewport]
  )

  const onTouchMove = useCallback(
    (e) => {
      if (lockViewport) return
      if (e.touches.length !== 2 || !pinchStartRef.current) return
      e.preventDefault()
      const start = pinchStartRef.current
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1])
      const currentCenter = getTouchCenter(e.touches[0], e.touches[1])
      const scale = currentDistance / start.distance
      const newZoom = clampZoom(start.zoom * scale)
      setZoom(newZoom)
      // Pan like a map: move by the centroid delta (two-finger drag)
      const dx = currentCenter.x - start.center.x
      const dy = currentCenter.y - start.center.y
      setPan({ x: start.pan.x + dx, y: start.pan.y + dy })
    },
    [setZoom, setPan, clampZoom, lockViewport]
  )

  const onTouchEnd = useCallback(() => {
    if (pinchStartRef.current) pinchStartRef.current = null
    isInteractingRef.current = false
  }, [])

  // Mouse/trackpad pan: right mouse button or middle button drag (keeps left-click for drawing)
  const onPointerDown = useCallback(
    (e) => {
      if (!viewportRef.current) return
      if (lockViewport) return
      if (e.pointerType !== 'mouse') return
      // 1 = left, 2 = middle, 3 = right (for buttons). In PointerEvent: button 0/1/2.
      const isPanButton = e.button === 1 || e.button === 2
      if (!isPanButton) return
      e.preventDefault()
      isInteractingRef.current = true
      dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y }
      viewportRef.current.setPointerCapture?.(e.pointerId)
    },
    [pan.x, pan.y, lockViewport]
  )

  const onPointerMove = useCallback((e) => {
    if (!dragStartRef.current) return
    if (e.pointerType !== 'mouse') return
    e.preventDefault()
    const start = dragStartRef.current
    const dx = e.clientX - start.x
    const dy = e.clientY - start.y
    setPan({ x: start.panX + dx, y: start.panY + dy })
  }, [setPan])

  const onPointerUp = useCallback((e) => {
    if (e.pointerType !== 'mouse') return
    if (dragStartRef.current) dragStartRef.current = null
    isInteractingRef.current = false
    try {
      viewportRef.current?.releasePointerCapture?.(e.pointerId)
    } catch {
      // ignore
    }
  }, [])

  const onContextMenu = useCallback((e) => {
    // Prevent context menu when user is panning with right click
    if (isInteractingRef.current) e.preventDefault()
  }, [])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    if (isTouchDevice) return
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [onWheel, isTouchDevice])

  const fitLockedViewport = useCallback(() => {
    if (!lockViewport) return
    if (!contentSize?.w || !contentSize?.h) return
    const el = viewportRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const vw = Math.max(1, rect.width)
    const vh = Math.max(1, rect.height)
    // Edge-to-edge template: fill the whole viewport (may crop one axis).
    const scale = Math.max(vw / contentSize.w, vh / contentSize.h)
    const nextZoom = Number.isFinite(scale) && scale > 0 ? scale : 1
    const nextPanX = Math.round((vw - contentSize.w * nextZoom) / 2)
    const nextPanY = Math.round((vh - contentSize.h * nextZoom) / 2)
    setZoom((prev) => (Math.abs(prev - nextZoom) < 0.001 ? prev : nextZoom))
    setPan((prev) => (
      Math.abs((prev?.x ?? 0) - nextPanX) < 1 && Math.abs((prev?.y ?? 0) - nextPanY) < 1
        ? prev
        : { x: nextPanX, y: nextPanY }
    ))
  }, [lockViewport, contentSize?.w, contentSize?.h, setZoom, setPan])

  useEffect(() => {
    fitLockedViewport()
  }, [fitLockedViewport])

  useEffect(() => {
    if (!lockViewport) return
    const el = viewportRef.current
    if (!el) return
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => fitLockedViewport())
      ro.observe(el)
      return () => ro.disconnect()
    }
    const onResize = () => fitLockedViewport()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [lockViewport, fitLockedViewport])

  return (
    <div
      ref={viewportRef}
      className="canvas-viewport"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onContextMenu={onContextMenu}
    >
      <div
        className={`canvas-viewport-inner ${isInteractingRef.current ? 'is-interacting' : ''}`}
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: 'center center',
        }}
      >
        {children}
      </div>
      {!lockViewport && !isTouchDevice && (
      <div className="canvas-zoom-controls" role="group" aria-label={lang === 'en' ? 'Zoom' : 'זום'}>
        <button
          type="button"
          className="canvas-zoom-btn"
          onClick={zoomOut}
          disabled={zoom <= MIN_ZOOM}
          aria-label={lang === 'en' ? 'Zoom out' : 'הקטן'}
        >
          −
        </button>
        <span className="canvas-zoom-value" aria-live="polite">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          className="canvas-zoom-btn"
          onClick={zoomIn}
          disabled={zoom >= MAX_ZOOM}
          aria-label={lang === 'en' ? 'Zoom in' : 'הגדל'}
        >
          +
        </button>
        <button
          type="button"
          className="canvas-zoom-btn canvas-zoom-reset"
          onClick={zoomReset}
          title={lang === 'en' ? 'Reset zoom' : 'איפוס זום'}
          aria-label={lang === 'en' ? 'Reset zoom' : 'איפוס זום'}
        >
          {lang === 'en' ? '1:1' : '1:1'}
        </button>
      </div>
      )}
    </div>
  )
}
